import { useAuth } from '@clerk/clerk-react';
import { useCallback, useRef } from 'react';
import { apiCall } from '../utils/auth';
import { API_ENDPOINTS } from '../config/clerkApi';

export const useActivityTracker = () => {
  const { getToken } = useAuth();
  const refreshCallbackRef = useRef(null);

  const setRefreshCallback = useCallback((callback) => {
    refreshCallbackRef.current = callback;
  }, []);

  const trackActivity = useCallback(async (activityType, count = 1) => {
    try {
      if (!getToken) {
        console.warn('No authentication token available');
        return null;
      }

      const response = await apiCall(API_ENDPOINTS.TRACK_ACTIVITY, {
        method: 'POST',
        body: JSON.stringify({
          activity_type: activityType,
          count: count
        })
      }, getToken);

      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Activity tracked: ${activityType}`, data);
        
        // Trigger calendar refresh if callback is available
        if (refreshCallbackRef.current) {
          await refreshCallbackRef.current();
        }
        
        return data;
      } else {
        console.error(`❌ Failed to track activity: ${activityType}`);
        return null;
      }
    } catch (error) {
      console.error('Activity tracking error:', error);
      return null;
    }
  }, [getToken]);

  const getContributions = useCallback(async (days = 365, year = null) => {
    try {
      if (!getToken) {
        console.warn('No authentication token available');
        return null;
      }

      const params = new URLSearchParams();
      if (days) params.append('days', days);
      if (year) params.append('year', year);

      const url = `${API_ENDPOINTS.CONTRIBUTIONS}?${params.toString()}`;
      
      const response = await apiCall(url, {
        method: 'GET'
      }, getToken);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Contributions loaded:', data);
        return data;
      } else {
        console.error('❌ Failed to load contributions');
        return null;
      }
    } catch (error) {
      console.error('Contributions loading error:', error);
      return null;
    }
  }, [getToken]);

  return {
    trackActivity,
    getContributions,
    setRefreshCallback
  };
};

// Activity types constants
export const ACTIVITY_TYPES = {
  LOGIN: 'login',
  VIDEO_VIEW: 'video_view',
  LESSON_COMPLETE: 'lesson_complete',
  QUIZ_COMPLETE: 'quiz_complete',
  MINDMAP_CREATE: 'mindmap_create',
  PROFILE_UPDATE: 'profile_update'
}; 